import { z } from 'zod';

import { SELLER_TYPES, SERVICE_HISTORY, VEHICLE_CLASSES } from 'Components/VehicleList/VehicleListFilters.consts';
import {
	zodCoerceOptionalNullableNumber,
	zodCoerceOptionalNumber,
	zodOptionalBoolean,
	zodOptionalOrTrueStringBoolean,
	zodOptionalString,
	zodUnionStringEnumOrArrayOfEnum,
	zodUnionStringOrArrayOfString,
} from 'Services/zod.helpers';
import vehicleListFilterConfig from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterConfig';
import { Fuel } from 'Types/fuel';
import { ExteriorGrade, NumericGrade } from 'Types/grade';
import { Transmission } from 'Types/transmission';
import { LIST_TYPES } from 'Utilities/consts';
import { VEHICLE_SORT_OPTIONS_SORT_ONLY } from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder.const';

const {
	ageFrom,
	ageTo,
	displayPriceFrom,
	displayPriceTo,
	excludeVehiclesOnFinance,
	exteriorGrade,
	fuel,
	includeOnlyInternalTestingVehicles,
	includeSold,
	includeUnderOffer,
	isAvailableForCollection,
	isPaidListing,
	make,
	maxDistance,
	mileageFrom,
	mileageTo,
	model,
	numericGrade,
	order,
	previousKeepersCountFrom,
	previousKeepersCountTo,
	selectedVehicle,
	sellerType,
	serviceHistory,
	transmission,
	vehicleClass,
} = vehicleListFilterConfig;

const AgeFromSchema = zodCoerceOptionalNullableNumber().catch(ageFrom.value);
const AgeToSchema = zodCoerceOptionalNullableNumber().catch(ageTo.value);
const DisplayPriceFromSchema = zodCoerceOptionalNullableNumber().catch(displayPriceFrom.value);
const DisplayPriceToSchema = zodCoerceOptionalNullableNumber().catch(displayPriceTo.value);
const ExcludeVehiclesOnFinanceSchema = zodOptionalOrTrueStringBoolean().catch(excludeVehiclesOnFinance.value);
const ExteriorGradeSchema = zodUnionStringEnumOrArrayOfEnum(ExteriorGrade).optional().catch(exteriorGrade.value);
const NumericGradeSchema = zodUnionStringEnumOrArrayOfEnum(NumericGrade).optional().catch(numericGrade.value);
const FuelSchema = zodUnionStringEnumOrArrayOfEnum(Fuel).optional().catch(fuel.value);
const IncludeSoldSchema = zodOptionalBoolean().catch(includeSold.value);
const IncludeUnderOfferSchema = zodOptionalBoolean().catch(includeUnderOffer.value);
const IsAvailableForCollectionSchema = zodOptionalBoolean().catch(isAvailableForCollection.value);
const IsPaidListingSchema = zodOptionalOrTrueStringBoolean().catch(isPaidListing.value);
const ListTypeSchema = z.nativeEnum(LIST_TYPES).optional().catch(undefined);

const MakeSchema = z.preprocess((makeQuery) => {
	if (Array.isArray(makeQuery)) {
		return makeQuery;
	}
	if (makeQuery) {
		return [makeQuery];
	}
	return null;
}, z.array(z.string()).optional().catch(make.value));

const MaxDistanceSchema = zodCoerceOptionalNullableNumber().catch(maxDistance.value);
const MileageFromSchema = zodCoerceOptionalNullableNumber().catch(mileageFrom.value);
const MileageToSchema = zodCoerceOptionalNullableNumber().catch(mileageTo.value);
const ModelSchema = zodUnionStringOrArrayOfString().optional().catch(model.value);
const OrderSchema = zodOptionalString().catch(order.value);
const PreviousKeepersCountFromSchema = zodCoerceOptionalNumber().nullable().catch(previousKeepersCountFrom.value);
const PreviousKeepersCountToSchema = zodCoerceOptionalNumber().nullable().catch(previousKeepersCountTo.value);
const SelectedVehicleSchema = zodCoerceOptionalNullableNumber().catch(selectedVehicle.value);
const SellerTypeSchema = zodUnionStringEnumOrArrayOfEnum(SELLER_TYPES).optional().catch(sellerType.value);
const ServiceHistorySchema = zodUnionStringEnumOrArrayOfEnum(SERVICE_HISTORY).optional().catch(serviceHistory.value);
const SortSchema = z
	.enum([...VEHICLE_SORT_OPTIONS_SORT_ONLY, ''])
	.optional()
	.catch('');
const InternalTestingVehiclesSchema = zodOptionalBoolean().catch(includeOnlyInternalTestingVehicles.value);
const TransmissionSchema = zodUnionStringEnumOrArrayOfEnum(Transmission).optional().catch(transmission.value);
const VehicleClassSchema = zodUnionStringEnumOrArrayOfEnum(VEHICLE_CLASSES).optional().catch(vehicleClass.value);

export const VehicleListQuerySchema = z.object({
	ageFrom: AgeFromSchema,
	ageTo: AgeToSchema,
	displayPriceFrom: DisplayPriceFromSchema,
	displayPriceTo: DisplayPriceToSchema,
	excludeVehiclesOnFinance: ExcludeVehiclesOnFinanceSchema,
	exteriorGrade: ExteriorGradeSchema,
	fuel: FuelSchema,
	includeOnlyInternalTestingVehicles: InternalTestingVehiclesSchema,
	includeSold: IncludeSoldSchema,
	includeUnderOffer: IncludeUnderOfferSchema,
	isAvailableForCollection: IsAvailableForCollectionSchema,
	isPaidListing: IsPaidListingSchema,
	listType: ListTypeSchema,
	make: MakeSchema,
	maxDistance: MaxDistanceSchema,
	mileageFrom: MileageFromSchema,
	mileageTo: MileageToSchema,
	model: ModelSchema,
	numericGrade: NumericGradeSchema,
	order: OrderSchema,
	previousKeepersCountFrom: PreviousKeepersCountFromSchema,
	previousKeepersCountTo: PreviousKeepersCountToSchema,
	selectedVehicle: SelectedVehicleSchema,
	sellerType: SellerTypeSchema,
	serviceHistory: ServiceHistorySchema,
	sort: SortSchema,
	transmission: TransmissionSchema,
	vehicleClass: VehicleClassSchema,
});

export type SanitisedVehicleListQuery = z.infer<typeof VehicleListQuerySchema>;
